import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, interval, map, Observable, of, startWith, switchMap, takeWhile } from 'rxjs';

@Injectable()
export class TimerService {
  private timer$ = new BehaviorSubject<number | null>(null);
  private timerControl$ = new BehaviorSubject<boolean>(false);

  readonly remainingSeconds$: Observable<number | null> = this.timerControl$.pipe(
    switchMap((isRunning) => {
      if (!isRunning) return of(null); // Stop the timer

      return this.timer$.pipe(
        switchMap((totalSeconds) => {
          if (totalSeconds === null) return of(null);

          return interval(1000).pipe(
            startWith(0), // Emit 0 immediately to start the countdown
            map((secondsPassed) => totalSeconds - secondsPassed),
            takeWhile((remainingSeconds) => remainingSeconds >= 0),
            finalize(() => this.timerControl$.next(false)), // Reset running flag
          );
        }),
      );
    }),
  );

  public start(waitSeconds: number): void {
    if (waitSeconds <= 0) {
      return;
    }
    //round to the nearest second
    waitSeconds = Math.round(waitSeconds);

    this.timer$.next(waitSeconds);
    this.timerControl$.next(true);
  }

  public stop(): void {
    this.timerControl$.next(false);
  }

  public getRemainingTime(seconds: number | null): string {
    if (!seconds) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }

  public calculateWaitDurationInSeconds(timeToRespond?: Date): number {
    if (!timeToRespond) {
      return 0;
    }
    const now = new Date().getTime();
    const timeToRespondMilliseconds = timeToRespond.getTime();

    return Math.round(Math.max(0, (timeToRespondMilliseconds - now) / 1000));
  }
}
